import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import request from '../../utils/request';
import PageLayout from '../layout/PageLayout';
import DashboardFilterBar from './DashboardFilterBar';
import KpiStrip from './KpiStrip';
import KpiRail from './KpiRail';
import DashboardCanvas from './DashboardCanvas';
import InsightsPanel from './InsightsPanel';
import RecordsSheet from './RecordsSheet';
import DashboardDrawer from './DashboardDrawer';
import { CardSkeleton, ErrorState } from './states';
import { formatRangeLabel, resolveRange } from './dateRange';
import { PMS_LABELS, fmtMoney, fmtNumber } from './derive';

const DEFAULT_FILTERS = {
  range: '30d',
  customStart: null,
  customEnd: null,
  pms: 'all',
  hostLifecycle: 'all',
  state: 'all',
  search: '',
  orderStatus: 'all',
  deliveryFeeType: 'all',
  storeLocation: '',
};

const DEFAULT_RECORD_STATE = {
  page: 0,
  sort: { key: 'createdAt', dir: 'desc' },
  rows: [],
  totalRows: 0,
  loading: false,
  error: null,
};

const VIEW_TITLES = {
  growth: 'Host records',
  hosts: 'Host records',
  pms: 'PMS distribution',
  drivers: 'Driver records',
  orders: 'Order records',
  properties: 'Property records',
};

const mapViewToEntity = (view) => {
  switch (view) {
    case 'growth':
    case 'hosts':
      return 'hosts';
    case 'drivers':
      return 'drivers';
    case 'orders':
      return 'orders';
    case 'properties':
      return 'properties';
    case 'pms':
      return 'pms';
    default:
      return 'orders';
  }
};

const getDefaultSortForView = (view) => {
  switch (view) {
    case 'growth':
      return { key: 'createdAt', dir: 'desc' };
    case 'hosts':
      return { key: 'orderCount', dir: 'desc' };
    case 'drivers':
      return { key: 'createdAt', dir: 'desc' };
    case 'orders':
      return { key: 'inStockDate', dir: 'desc' };
    case 'properties':
      return { key: 'createdAt', dir: 'desc' };
    case 'pms':
      return { key: 'count', dir: 'desc' };
    default:
      return { key: 'createdAt', dir: 'desc' };
  }
};

const buildDashboardParams = (filters, range, overrides = {}) => {
  const params = new URLSearchParams();
  params.set('startDate', range.start.toISOString());
  params.set('endDate', range.end.toISOString());

  const values = {
    pms: filters.pms,
    hostLifecycle: filters.hostLifecycle,
    state: filters.state,
    search: filters.search,
    orderStatus: filters.orderStatus,
    deliveryFeeType: filters.deliveryFeeType,
    storeLocation: filters.storeLocation,
    ...overrides,
  };

  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, value);
    }
  });

  return params;
};

const buildSeriesLabels = (rows = []) =>
  rows.map((row) => ({
    ...row,
    label: row.key
      ? new Date(`${row.key}T00:00:00.000Z`).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        })
      : '',
  }));

function buildHostColumns() {
  return [
    {
      key: 'name',
      header: 'Host',
      searchable: true,
      sortable: true,
      accessor: (row) => row.name,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-zinc-100 text-[10px] font-semibold text-zinc-600">
            {(row.name || '?').slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold text-zinc-900">{row.name}</p>
            <p className="truncate text-[10px] text-zinc-500">{row.email || '—'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'pmsType',
      header: 'PMS',
      sortable: true,
      accessor: (row) => PMS_LABELS[row.pmsType] || '—',
    },
    {
      key: 'propertyCount',
      header: 'Props',
      align: 'right',
      sortable: true,
      accessor: (row) => row.propertyCount || 0,
    },
    {
      key: 'orderCount',
      header: 'Orders',
      align: 'right',
      sortable: true,
      accessor: (row) => row.orderCount || 0,
    },
    {
      key: 'aov',
      header: 'AOV',
      align: 'right',
      sortable: true,
      accessor: (row) => row.aov || 0,
      cell: (row) => fmtMoney(row.aov || 0),
    },
    {
      key: 'revenue',
      header: 'Volume',
      align: 'right',
      sortable: true,
      accessor: (row) => row.revenue || 0,
      cell: (row) => fmtMoney(row.revenue || 0),
    },
    {
      key: 'isNew',
      header: 'Lifecycle',
      sortable: true,
      accessor: (row) => (row.isNew ? 'New' : 'Legacy'),
      cell: (row) => (
        <span
          className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
            row.isNew ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-600'
          }`}
        >
          {row.isNew ? 'New' : 'Legacy'}
        </span>
      ),
    },
  ];
}

function buildOrderColumns() {
  return [
    {
      key: 'uniqueId',
      header: 'Order',
      searchable: true,
      sortable: true,
      accessor: (row) => row.uniqueId || row._id,
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-zinc-900">
            {row.uniqueId || String(row._id).slice(-6)}
          </p>
          <p className="truncate text-[10px] text-zinc-500">
            {row?.property?.address?.line1 || '—'}
          </p>
        </div>
      ),
    },
    {
      key: 'inStockDate',
      header: 'In stock',
      sortable: true,
      sortAccessor: (row) => new Date(row.inStockDate || 0).getTime(),
      cell: (row) =>
        row.inStockDate
          ? new Date(row.inStockDate).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '—',
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      searchable: true,
      cell: (row) => (
        <span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-zinc-700">
          {row.status}
        </span>
      ),
    },
    {
      key: 'storeLocation',
      header: 'Store',
      sortable: true,
      searchable: true,
      accessor: (row) => row.storeLocation || '—',
    },
    {
      key: 'revenue',
      header: 'Revenue',
      align: 'right',
      sortable: true,
      accessor: (row) => row._revenue || 0,
      cell: (row) => fmtMoney(row._revenue || 0),
    },
  ];
}

function buildDriverColumns() {
  return [
    {
      key: 'name',
      header: 'Driver',
      searchable: true,
      sortable: true,
      accessor: (row) => row.name,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-emerald-50 text-[10px] font-semibold text-emerald-700">
            {(row.name || '?').slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold text-zinc-900">{row.name}</p>
            <p className="truncate text-[10px] text-zinc-500">{row.email || '—'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Acquired',
      sortable: true,
      sortAccessor: (row) => new Date(row.createdAt || 0).getTime(),
      cell: (row) =>
        row.createdAt
          ? new Date(row.createdAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '—',
    },
    {
      key: 'market',
      header: 'Market',
      sortable: true,
      searchable: true,
      accessor: (row) => row.market || '—',
    },
    {
      key: 'active',
      header: 'Status',
      sortable: true,
      accessor: (row) => (row.active ? 'Active' : 'Inactive'),
      cell: (row) => (
        <span
          className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
            row.active ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
          }`}
        >
          {row.active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ];
}

function buildPropertyColumns() {
  return [
    {
      key: 'address',
      header: 'Address',
      searchable: true,
      sortable: true,
      accessor: (row) => `${row?.address?.line1 || ''} ${row?.address?.city || ''}`,
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-zinc-900">
            {row?.address?.line1 || row?.listingTitle || 'Property'}
          </p>
          <p className="truncate text-[10px] text-zinc-500">
            {row?.address?.city || ''}{row?.address?.state ? `, ${row.address.state}` : ''}
          </p>
        </div>
      ),
    },
    {
      key: 'hostName',
      header: 'Host',
      searchable: true,
      sortable: true,
      accessor: (row) => [row.hostName, row.hostEmail, row.hostCompanyName].filter(Boolean).join(' '),
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-zinc-900">{row.hostName || '—'}</p>
          <p className="truncate text-[10px] text-zinc-500">{row.hostEmail || row.hostCompanyName || '—'}</p>
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      sortAccessor: (row) => new Date(row.createdAt || 0).getTime(),
      cell: (row) =>
        row.createdAt
          ? new Date(row.createdAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '—',
    },
    {
      key: 'propertySource',
      header: 'Source',
      sortable: true,
      searchable: true,
      accessor: (row) => PMS_LABELS[row.propertySource] || row.propertySource || '—',
    },
  ];
}

function buildPmsColumns(rows) {
  return [
    {
      key: 'label',
      header: 'PMS',
      searchable: true,
      sortable: true,
      cell: (row) => <span className="font-semibold text-zinc-900">{row.label}</span>,
    },
    {
      key: 'count',
      header: 'Hosts',
      align: 'right',
      sortable: true,
      cell: (row) => fmtNumber(row.count || 0),
    },
    {
      key: 'properties',
      header: 'Properties',
      align: 'right',
      sortable: true,
      cell: (row) => fmtNumber(row.properties || 0),
    },
    {
      key: 'share',
      header: 'Host share',
      align: 'right',
      sortable: true,
      sortAccessor: (row) => row.count || 0,
      cell: (row) => {
        const total = rows.reduce((sum, current) => sum + (current.count || 0), 0);
        return total ? `${Math.round(((row.count || 0) / total) * 100)}%` : '0%';
      },
    },
  ];
}

export default function DashboardOverview() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [drawer, setDrawer] = useState({ kind: null, payload: null });
  const [activeView, setActiveView] = useState('growth');
  const [recordsView, setRecordsView] = useState(null);
  const [recordsState, setRecordsState] = useState({
    ...DEFAULT_RECORD_STATE,
    sort: getDefaultSortForView('growth'),
  });
  const summaryRequestRef = useRef(0);
  const recordsRequestRef = useRef(0);

  const range = useMemo(
    () => resolveRange(filters.range, filters.customStart, filters.customEnd),
    [filters.range, filters.customStart, filters.customEnd]
  );

  const rangeLabel = useMemo(
    () => formatRangeLabel(filters.range, filters.customStart, filters.customEnd),
    [filters.range, filters.customStart, filters.customEnd]
  );

  const loadSummary = useCallback(async () => {
    const requestId = summaryRequestRef.current + 1;
    summaryRequestRef.current = requestId;
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const params = buildDashboardParams(filters, range);
      const { data } = await request.get(`/admin/dashboard/summary?${params.toString()}`);
      if (summaryRequestRef.current !== requestId) return;
      setSummary({
        ...data,
        series: {
          hosts: buildSeriesLabels(data?.series?.hosts || []),
          drivers: buildSeriesLabels(data?.series?.drivers || []),
          orders: buildSeriesLabels(data?.series?.orders || []),
        },
      });
    } catch (error) {
      if (summaryRequestRef.current !== requestId) return;
      setSummaryError(error?.response?.data?.error || 'Failed to load dashboard summary');
      setSummary(null);
    } finally {
      if (summaryRequestRef.current !== requestId) return;
      setSummaryLoading(false);
    }
  }, [filters, range]);

  const loadRecords = useCallback(async () => {
    if (!recordsView) return;
    const requestId = recordsRequestRef.current + 1;
    recordsRequestRef.current = requestId;
    const entity = mapViewToEntity(recordsView);
    setRecordsState((current) => ({ ...current, loading: true, error: null }));
    try {
      const params = buildDashboardParams(filters, range, {
        entity,
        page: String((recordsState.page || 0) + 1),
        pageSize: '12',
        sortKey: recordsState.sort?.key || '',
        sortDir: recordsState.sort?.dir || 'desc',
      });
      const { data } = await request.get(`/admin/dashboard/records?${params.toString()}`);
      if (recordsRequestRef.current !== requestId) return;
      setRecordsState((current) => ({
        ...current,
        rows: data?.rows || [],
        totalRows: data?.totalRows || 0,
        loading: false,
      }));
    } catch (error) {
      if (recordsRequestRef.current !== requestId) return;
      setRecordsState((current) => ({
        ...current,
        rows: [],
        totalRows: 0,
        loading: false,
        error: error?.response?.data?.error || 'Failed to load records',
      }));
    }
  }, [filters, range, recordsState.page, recordsState.sort, recordsView]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    if (recordsView) {
      loadRecords();
    }
  }, [loadRecords, recordsView]);

  const metrics = useMemo(() => {
    const topPms = summary?.kpis?.topPms;

    return [
      {
        id: 'hosts',
        label: 'Hosts',
        value: fmtNumber(summary?.kpis?.hosts || 0),
        sublabel: 'created in range',
        accent: 'indigo',
        tab: 'growth',
      },
      {
        id: 'drivers',
        label: 'Drivers',
        value: fmtNumber(summary?.kpis?.drivers || 0),
        sublabel: 'acquired in range',
        accent: 'emerald',
        tab: 'drivers',
      },
      {
        id: 'properties',
        label: 'Properties',
        value: fmtNumber(summary?.kpis?.properties || 0),
        sublabel: 'added in range',
        accent: 'sky',
        tab: 'properties',
      },
      {
        id: 'activeHosts',
        label: 'Active hosts',
        value: fmtNumber(summary?.kpis?.activeHosts || 0),
        sublabel: 'verified & onboarded',
        accent: 'emerald',
        tab: 'hosts',
      },
      {
        id: 'activeProps',
        label: 'Active props',
        value: fmtNumber(summary?.kpis?.activeProperties || 0),
        sublabel: '2+ orders in scope',
        accent: 'sky',
        tab: 'properties',
      },
      {
        id: 'orders',
        label: 'Orders',
        value: fmtNumber(summary?.kpis?.orders || 0),
        sublabel: 'in stock date scoped',
        accent: 'zinc',
        tab: 'orders',
      },
      {
        id: 'aov',
        label: 'Avg order',
        value: fmtMoney(summary?.kpis?.aov || 0),
        sublabel: `${fmtMoney(summary?.kpis?.revenue || 0)} volume`,
        accent: 'amber',
        tab: 'orders',
      },
      {
        id: 'topPms',
        label: 'Top PMS',
        value: topPms ? PMS_LABELS[topPms.id] || topPms.id : '—',
        sublabel: topPms ? `${fmtNumber(topPms.count)} hosts` : 'No PMS signal',
        accent: 'indigo',
        tab: 'pms',
        filter: topPms ? { pms: topPms.id } : null,
      },
    ];
  }, [summary]);

  const subtitle = useMemo(() => {
    const hosts = summary?.rowCounts?.hosts || 0;
    const orders = summary?.rowCounts?.orders || 0;
    return `${rangeLabel} · ${fmtNumber(hosts)} hosts · ${fmtNumber(orders)} orders`;
  }, [rangeLabel, summary]);

  const onChangeFilters = useCallback((patch) => {
    setFilters((current) => ({ ...current, ...patch }));
    setRecordsState((current) => ({ ...current, page: 0 }));
  }, []);

  const onResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setRecordsState((current) => ({
      ...current,
      page: 0,
      sort: getDefaultSortForView(recordsView || activeView),
    }));
  }, [activeView, recordsView]);

  const onRefresh = useCallback(() => {
    loadSummary();
    if (recordsView) loadRecords();
  }, [loadRecords, loadSummary, recordsView]);

  const openHostDrawer = useCallback((host) => setDrawer({ kind: 'host', payload: host }), []);
  const openPropertyDrawer = useCallback((property) => setDrawer({ kind: 'property', payload: property }), []);
  const openPmsDrawer = useCallback((pmsId) => setDrawer({ kind: 'pms', payload: pmsId }), []);
  const openOrderDrawer = useCallback((order) => setDrawer({ kind: 'order', payload: order }), []);
  const closeDrawer = useCallback(() => setDrawer({ kind: null, payload: null }), []);

  const openRecords = useCallback((view) => {
    setRecordsView(view);
    setRecordsState(() => ({
      ...DEFAULT_RECORD_STATE,
      sort: getDefaultSortForView(view),
    }));
  }, []);

  const closeRecords = useCallback(() => {
    setRecordsView(null);
  }, []);

  const onMetricSelect = useCallback((metric) => {
    if (metric.tab) setActiveView(metric.tab);
    if (metric.filter) {
      setFilters((current) => ({ ...current, ...metric.filter }));
      setRecordsState((current) => ({ ...current, page: 0 }));
    }
  }, []);

  const activeMetricId = useMemo(() => {
    const mapping = {
      growth: 'hosts',
      hosts: 'activeHosts',
      drivers: 'drivers',
      orders: 'orders',
      properties: 'properties',
      pms: 'topPms',
      map: 'orders',
    };
    return mapping[activeView] || 'hosts';
  }, [activeView]);

  const recordsConfig = useMemo(() => {
    if (!recordsView) return null;

    const entity = mapViewToEntity(recordsView);
    const rows =
      entity === 'pms'
        ? (recordsState.rows || []).map((row) => ({
            ...row,
            label: PMS_LABELS[row.id] || row.label || row.id,
          }))
        : recordsState.rows || [];

    const base = {
      title: VIEW_TITLES[recordsView] || 'Records',
      rows,
      totalRows: recordsState.totalRows || 0,
      page: recordsState.page || 0,
      onPageChange: (page) => setRecordsState((current) => ({ ...current, page })),
      sort: recordsState.sort,
      onSortChange: (sort) => setRecordsState((current) => ({ ...current, sort, page: 0 })),
      query: filters.search,
      onQueryChange: (search) => onChangeFilters({ search }),
      manualMode: true,
    };

    if (entity === 'hosts') {
      return {
        ...base,
        columns: buildHostColumns(),
        onRowClick: openHostDrawer,
        searchPlaceholder: 'Search hosts by name, email, company…',
        initialSort: getDefaultSortForView(recordsView),
      };
    }
    if (entity === 'drivers') {
      return {
        ...base,
        columns: buildDriverColumns(),
        searchPlaceholder: 'Search drivers by name, email, market…',
        initialSort: getDefaultSortForView(recordsView),
      };
    }
    if (entity === 'orders') {
      return {
        ...base,
        columns: buildOrderColumns(),
        onRowClick: openOrderDrawer,
        searchPlaceholder: 'Search orders by ID, store, property, host…',
        initialSort: getDefaultSortForView(recordsView),
      };
    }
    if (entity === 'properties') {
      return {
        ...base,
        columns: buildPropertyColumns(),
        onRowClick: openPropertyDrawer,
        searchPlaceholder: 'Search properties by address, city, host…',
        initialSort: getDefaultSortForView(recordsView),
      };
    }
    return {
      ...base,
      columns: buildPmsColumns(rows),
      onRowClick: (row) => openPmsDrawer(row.id),
      searchPlaceholder: 'Search PMS…',
      initialSort: getDefaultSortForView(recordsView),
    };
  }, [filters.search, onChangeFilters, openHostDrawer, openOrderDrawer, openPmsDrawer, openPropertyDrawer, recordsState.page, recordsState.rows, recordsState.sort, recordsState.totalRows, recordsView]);

  if (summaryLoading && !summary) {
    return (
      <PageLayout title="Platform Dashboard" subtitle="Loading…" showSearch={false} fullWidth>
        <div className="space-y-3">
          <CardSkeleton className="h-24" />
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
            <CardSkeleton className="lg:col-span-2 h-[540px]" />
            <CardSkeleton className="lg:col-span-7 h-[540px]" />
            <CardSkeleton className="lg:col-span-3 h-[540px]" />
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Platform Dashboard" subtitle={subtitle} showSearch={false} fullWidth>
      <div className="space-y-3">
        <DashboardFilterBar
          activeView={activeView}
          filters={filters}
          onChange={onChangeFilters}
          onReset={onResetFilters}
          onRefresh={onRefresh}
          refreshing={summaryLoading || recordsState.loading}
        />

        {summaryError && !summary ? (
          <ErrorState
            title="Could not load dashboard"
            description={summaryError}
            onRetry={onRefresh}
          />
        ) : null}

        <div className="hidden lg:grid lg:grid-cols-12 lg:gap-3" style={{ height: 'calc(100vh - 220px)', minHeight: 540 }}>
          <div className="lg:col-span-2 min-h-0">
            <KpiRail metrics={metrics} activeId={activeMetricId} onSelect={onMetricSelect} />
          </div>
          <div className="lg:col-span-7 min-h-0">
            <DashboardCanvas
              activeView={activeView}
              onChangeView={setActiveView}
              onOpenRecords={openRecords}
              rangeLabel={rangeLabel}
              summary={summary}
              onSelectHost={openHostDrawer}
              onSelectPms={openPmsDrawer}
            />
          </div>
          <div className="lg:col-span-3 min-h-0">
            <InsightsPanel
              summary={summary}
              loading={summaryLoading}
              error={summaryError}
              onSelectHost={openHostDrawer}
              onSelectProperty={openPropertyDrawer}
              onSelectPms={openPmsDrawer}
            />
          </div>
        </div>

        <div className="space-y-3 lg:hidden">
          <KpiStrip metrics={metrics} activeId={activeMetricId} onSelect={onMetricSelect} />
          <div className="min-h-[520px]">
            <DashboardCanvas
              activeView={activeView}
              onChangeView={setActiveView}
              onOpenRecords={openRecords}
              rangeLabel={rangeLabel}
              summary={summary}
              onSelectHost={openHostDrawer}
              onSelectPms={openPmsDrawer}
            />
          </div>
        </div>
      </div>

      <DashboardDrawer
        open={Boolean(drawer.kind)}
        kind={drawer.kind}
        payload={drawer.payload}
        onClose={closeDrawer}
        filters={{
          startDate: range.start.toISOString(),
          endDate: range.end.toISOString(),
          pms: filters.pms,
          hostLifecycle: filters.hostLifecycle,
          state: filters.state,
          search: filters.search,
          orderStatus: filters.orderStatus,
          deliveryFeeType: filters.deliveryFeeType,
          storeLocation: filters.storeLocation,
        }}
        hosts={[]}
        onOpenHost={openHostDrawer}
        onOpenProperty={openPropertyDrawer}
      />

      <RecordsSheet open={Boolean(recordsView)} onClose={closeRecords} config={recordsConfig} />
    </PageLayout>
  );
}
